import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { difference, flatten, keys, omit, set } from 'lodash';
import { CanadaProvinces } from 'models/enums/CanadaProvinces';
import productDeleteApi from 'models/productManagement/product/delete/Api.Product.Delete';
import Product from 'models/productManagement/product/Model.Product';
import productSaveApi from 'models/productManagement/product/save/Api.Product.Save';
import productDocumentDeleteApi from 'models/productManagement/productDocument/delete/Api.ProductDocument.Delete';
import productDocumentSaveApi from 'models/productManagement/productDocument/save/Api.ProductDocument.Save';
import productDocumentUpdateAllApi from 'models/productManagement/productDocument/updateAll/Api.ProductDocument.UpdateAll';
import { useEffect, useMemo, useState } from 'react';
import { productIndexRoute } from './Product.Index';
import productCloneApi from 'models/productManagement/product/clone/Api.Product.Clone';
import productDocumentReplaceApi from 'models/productManagement/productDocument/replace/Api.ProductDocument.Replace';
import productDocumentOrderApi from 'models/productManagement/productDocument/order/Api.ProductDocument.Order';

export enum ProductAction {
  EDIT = 'edit',
  DELETE = 'delete',
  CLONE = 'clone',
}
export enum ProductEditTab {
  INFO = 'info',
  DOCUMENTS = 'documents',
  DOC_TEMPLATES = 'docTemplates',
  TAXES = 'taxes',
  PROVINCE_GROUPS = 'provinceGroups',
}
export type VerifyProductAction = Utils.VerifyExtends<
  Module.ProductManagement.Product.Actions,
  Utils.ValueOf<typeof ProductAction>
>;
export type VerifyProductEditTab = Utils.VerifyExtends<
  Module.ProductManagement.Product.EditTab,
  Utils.ValueOf<typeof ProductEditTab>
>;
export default function useProduct() {
  const { permissions, user } = useAuthStore();

  const { lang } = getLocales();
  const pageRouter = usePageRouter<Module.ProductManagement.Product.Params, Module.ProductManagement.Product.Query>({
    route: productIndexRoute,
  });
  const productId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;
  const [isLoading, setIsLoading] = useState(false);
  const canView =
    permissions.CREATE_PRODUCT ||
    permissions.EDIT_PRODUCT ||
    permissions.DELETE_PRODUCT ||
    permissions.EDIT_PRODUCTTAX ||
    permissions.VIEW_CUSTOMPRICEPOINT ||
    permissions.CREATE_CUSTOMPRICEPOINT ||
    permissions.EDIT_CUSTOMPRICEPOINT ||
    permissions.DELETE_CUSTOMPRICEPOINT ||
    permissions.VIEW_NONWARRANTYPRICEPOINT ||
    permissions.CREATE_NONWARRANTYPRICEPOINT ||
    permissions.EDIT_NONWARRANTYPRICEPOINT ||
    permissions.DELETE_NONWARRANTYPRICEPOINT ||
    permissions.VIEW_PRODUCTTIERPRICEPOINT ||
    permissions.CREATE_PRODUCTTIERPRICEPOINT ||
    permissions.EDIT_PRODUCTTIERPRICEPOINT ||
    permissions.DELETE_PRODUCTTIERPRICEPOINT ||
    permissions.VIEW_PRODUCTPRICINGPLAN ||
    permissions.CREATE_PRODUCTPRICINGPLAN ||
    permissions.EDIT_PRODUCTPRICINGPLAN ||
    permissions.DELETE_PRODUCTPRICINGPLAN;
  const listApi = useApi({
    action: Product.list,
    default: {
      limit: defaultLimit,
      offset: defaultOffset,
      dealerId: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ?? undefined,
    },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({ ...pageRouter.params }, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const {
    payload: product,
    execute: reloadProduct,
    isExecuting: isEditLoading,
    reset: resetProduct,
  } = useApi(
    {
      action: Product.find,
      body: { id: productId },
      default: null,
      wait: !productId,
    },
    [productId]
  );
  useEffect(() => {
    if (!productId) resetProduct(true);
  }, [productId]);

  const deleteApi = useApi({
    action: productDeleteApi,
    callback: () => {
      listApi.execute((body) => body);
      pageRouter.redirect({ ...pageRouter.params, action: undefined }, { ...pageRouter.query, editTab: undefined });
    },
    wait: true,
  });

  const cloneApi = useApi({
    action: productCloneApi,
    callback: (_, res) => {
      pageRouter.redirect(
        { id: res?.payload?.toString(), action: ProductAction.EDIT },
        { ...pageRouter.query, editTab: ProductEditTab.INFO }
      );
    },
    wait: true,
  });

  //#region Data editing
  const currentProduct = product?.toJSON();
  const currentProvinceTaxes = currentProduct?.productTaxes?.map((it) => it.provinceCode);
  const form = useForm<
    Utils.FormData<
      Model.IProduct & {
        productDocuments?: Array<Utils.FormData<Model.IProductDocument> & { isFileRepalced?: boolean }>;
      }
    >
  >(
    {
      default: {
        ...currentProduct,
        productTaxes: [
          ...(currentProduct?.productTaxes || []),
          ...difference(keys(CanadaProvinces), currentProvinceTaxes).map((provinceCode) => ({
            productId: currentProduct?.id || undefined,
            provinceCode,
            claimsProvincialSalesTax: 0,
            claimsGstHst: 0,
            contractProvincialSalesTax: 0,
            contractGstHst: 0,
          })),
        ],
      },
      onSubmit: async ({ productDocuments, ...product }) => {
        const productSaveResponse = await productSaveApi({
          ...product,
          productDocumentTemplates: product.productDocumentTemplates?.map((g) => (g.id <= 0 ? omit(g, 'id') : g)),
          productProvinceGroups: product.productProvinceGroups?.map((g) => (g.id <= 0 ? omit(g, 'id') : g)),
        });
        const productId = productSaveResponse.payload;
        if (productDocuments?.length) {
          await productDocumentUpdateAllApi(product.id || productId, {
            additionalFields: flatten(productDocuments.map((d) => d.productAdditionalFields)),
            productSignatureFields: flatten(productDocuments.map((d) => d.productSignatureFields)),
          });
          await productDocumentOrderApi(
            productDocuments?.reduce(
              (p, c) => ({
                ...p,
                [c.id]: c.orderNumber ?? 0,
              }),
              {}
            )
          );
        }
        for (const doc of productDocuments ?? []) {
          if (doc.isFileRepalced && doc.formFile) {
            await productDocumentReplaceApi({
              productId: product.id || productId,
              documentId: doc.id,
              formFile: doc.formFile,
            });
          }
        }
        if (productId?.toString() === pageRouter.params.id) {
          reloadProduct({ id: productId });
        } else {
          pageRouter.updateParams({
            action: ProductAction.EDIT,
            id: productId.toString(),
          });
        }
      },
      validation: (data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.code) errors.code = lang.mustNotBeEmpty;
        if (!data.providerId) errors.providerId = lang.mustNotBeEmpty;
        if (!data.pricingDistributionTemplateId) errors.pricingDistributionTemplateId = lang.mustNotBeEmpty;
        if (!data.productType) errors.productType = lang.mustNotBeEmpty;
        if (!data.category) set(errors, 'category._objectError', lang.mustNotBeEmpty);
        if (!data.insurers || data.insurers.length === 0) set(errors, 'insurers._objectError', lang.mustNotBeEmpty);
        if (!data.productTaxes || data.productTaxes.length === 0)
          set(errors, 'productTaxes._objectError', lang.mustNotBeEmpty);
        if (data.productProvinceGroups && data.productProvinceGroups.some((g) => !g.provinceCodes?.length))
          set(errors, 'productProvinceGroups.name', lang.mustNotBeEmpty);
        if (data.productProvinceGroups && data.productProvinceGroups.some((g) => !g.name))
          set(errors, 'productProvinceGroups.provinceCodes', lang.mustNotBeEmpty);
      },
    },
    [product]
  );
  const newDocuments = form.data.productDocuments?.filter((d) => !d.id) || [];
  const uploadedDocuments = form.data.productDocuments?.filter((d) => !!d.id) || [];
  //#endregion
  function reload() {
    reloadProduct((body) => body);
  }
  async function uploadFile(params: Pick<Model.IProductDocument, 'formFile' | 'productDocumentType' | 'orderNumber'>) {
    if (!params.formFile || !params.productDocumentType) return;
    setIsLoading(true);
    await productDocumentSaveApi({
      ...params,
      productId: product.id,
    });
    setIsLoading(false);
    reload();
    return;
  }
  async function deleteFile(documentId: number) {
    if (!documentId) return;
    setIsLoading(true);
    await productDocumentDeleteApi({ id: form.data?.id, documentId });
    setIsLoading(false);
    reload();
  }

  return {
    reload,
    uploadFile,
    deleteFile,
    newDocuments,
    uploadedDocuments,
    product,
    pageRouter,
    listApi,
    deleteApi,
    cloneApi,
    form,
    selected,
    permissions,
    isEditLoading: isEditLoading || isLoading,
    canView,
    views: {
      [ProductAction.DELETE]:
        pageRouter.params.action === ProductAction.DELETE && selected && permissions.DELETE_PRODUCT,
      [ProductAction.CLONE]: pageRouter.params.action === ProductAction.CLONE && selected && permissions.CREATE_PRODUCT,
      [ProductAction.EDIT]:
        pageRouter.params.action === ProductAction.EDIT &&
        ((permissions.CREATE_PRODUCT && !selected) || (permissions.EDIT_PRODUCT && selected)),
    },
  };
}

export function useProductProvider() {
  return useDataProvider<ReturnType<typeof useProduct>>();
}
